import axios from 'axios';

import { IDocument, DocumentType } from '../../types/models/document';
import { createError } from '../error';
import responseParser from '../response-parser';
import { API_URL, LAMBDA_PAYLOAD_SIZE_LIMIT } from '../../constants';

/**
 * Delete document by id
 *
 * @param {string} id Id of the document
 * @returns {Promise<void>}
 */
export async function deleteDocument(id: string, isOnS3 = true): Promise<void> {
  try {
    const url = `${API_URL}/documents/${id}?isOnS3=${isOnS3}`;

    await axios.delete(url);
  } catch (err) {
    throw createError(err);
  }
}

/**
 * Upload document
 *
 * @param {File} file Document to be uploaded
 * @returns {Promise<IDocument>}
 */
export async function uploadDocument(file: File, documentType?: DocumentType, stageId?: string): Promise<IDocument> {
  try {
    if (Number((file.size / 1024 / 1024).toFixed(2)) >= LAMBDA_PAYLOAD_SIZE_LIMIT) {
      const getUploadUrl = `${API_URL}/documents/presigned-upload`;
      const fileName = file.name;
      const params = {
        fileName,
        contentType: file.type,
        documentType,
      };

      const getUrlResponse = await axios.get(getUploadUrl, { params });
      const {
        presignedPost: { fields, url },
        id: documentId,
        date,
      } = getUrlResponse.data.result;

      const form = new FormData();
      Object.keys(fields).forEach((key: string) => {
        form.append(key, fields[key]);
      });
      form.append('file', file);

      try {
        await axios.post(url, form);
      } catch (error) {
        await deleteDocument(documentId, false);
        throw error;
      }

      return { documentType: documentType || DocumentType.GENERIC, id: documentId, date, fileName };
    } else {
      const url = `${API_URL}/documents`;
      const uploadFormData = new FormData();
      const config: any = {};
      uploadFormData.append('file', file);

      if (documentType) {
        uploadFormData.append('documentType', documentType);
      }
      if (stageId) {
        config.params = { stageId };
      }
      const response = await axios.post(url, uploadFormData, config);
      const data: IDocument = responseParser(response);
      return data;
    }
  } catch (err) {
    throw createError(err);
  }
}

/**
 * Get document (the binary) by id
 *
 * @param id Id of the document
 * @param download Flag to request response as an attachment
 * @returns {Promise<File>}
 */
export async function getDocument(id: string, download?: boolean): Promise<any> {
  try {
    const url = `${API_URL}/documents/${id}`;

    let response = await axios.get(url, {
      params: { download },
      headers: { accept: 'application/octet-stream' },
      responseType: 'blob',
    });
    let fileName: string;

    if (!response.headers['content-disposition']) {
      const json = JSON.parse(await response.data.text());
      const { url } = json.result;

      fileName = json.result.fileName;
      response = await axios.get(url, {
        headers: { accept: 'application/octet-stream' },
        responseType: 'blob',
      });
    } else {
      fileName = (response.headers['content-disposition'] || '').replace(/.+filename="(.+)"/g, '$1');
    }

    const type = response.headers['content-type'];

    return new File([response.data], fileName, { type });
  } catch (err) {
    throw createError(err);
  }
}
getDocument.QUERY_KEY = 'user:getDocument';

export async function getDocumentWithAction(id: string, download?: boolean): Promise<any> {
  try {
    const url = `${API_URL}/documents/${id}`;

    let response = await axios.get(url, {
      params: { download },
      headers: { accept: 'application/octet-stream' },
      responseType: 'blob',
    });
    let fileName: string;
    let action: 'view' | 'download';

    if (!response.headers['content-disposition']) {
      const json = JSON.parse(await response.data.text());
      const { url } = json.result;

      fileName = json.result.fileName;
      action = 'download';
      response = await axios.get(url, {
        headers: { accept: 'application/octet-stream' },
        responseType: 'blob',
      });
    } else {
      fileName = (response.headers['content-disposition'] || '').replace(/.+filename="(.+)"/g, '$1');
      action = 'view';
    }

    const type = response.headers['content-type'];

    return {
      file: new File([response.data], fileName, { type }),
      action,
    };
  } catch (err) {
    throw createError(err);
  }
}

/**
 * Get a list of documents
 * @param grouped Flag to get all latest documents group by the document type
 * @returns {Promise<File>}
 */
export async function getDocuments(grouped?: boolean): Promise<IDocument[]> {
  try {
    const url = `${API_URL}/documents`;
    const response = await axios.get(url, {
      params: { grouped },
    });
    return responseParser(response);
  } catch (err) {
    throw createError(err);
  }
}
getDocuments.QUERY_KEY = 'user:getDocuments';
